import React from "react";
import dayjs from "dayjs";
import { Modal, Form, Input, DatePicker, TimePicker, Button, Typography, Select, List } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getUnitAcronym } from "../../../../utils/ouberUtils";
import AdminInviteEmployeeModal from "../admin/AdminInviteEmployeeModal";

const { Text } = Typography;
const { TextArea } = Input;

const MIN_TOTAL_PASSENGERS = 3;

const UserRequestTripModal = ({ isOpen, onClose, onSave, initialValues, initialDate }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [companions, setCompanions] = React.useState([]);
  const [isInviteOpen, setIsInviteOpen] = React.useState(false);

  const currentUser = useSelector((state) => state.accountDetailSlice?.details);
  const userUnits = React.useMemo(() => {
    if (!currentUser?.positions) return [];
    
    const todayStr = dayjs().format("YYYY-MM-DD");
    let units = currentUser.positions
      .filter(p => {
        const endDate = p.pivot?.position_end_date || p.position_end_date;
        if (!endDate) return true;
        return endDate >= todayStr;
      })
      .map(p => getUnitAcronym(p.unit || p.department))
      .filter(u => u && u !== 'N/A');

    if (units.length === 0 && currentUser.department) {
      const dept = getUnitAcronym(currentUser.department);
      if (dept && dept !== 'N/A') {
        units = [dept];
      }
    }

    return [...new Set(units)];
  }, [currentUser]);

  React.useEffect(() => {
    if (isOpen) {
      if (userUnits.length === 1) {
        form.setFieldsValue({ unit: userUnits[0] });
      } else {
        form.setFieldsValue({ unit: undefined });
      }
    }
  }, [isOpen, userUnits, form]);

  React.useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setCompanions([]);
      if (initialValues) {
        form.setFieldsValue({
          tripDate: initialValues.date ? dayjs(initialValues.date) : null,
          pickupTime: initialValues.time ? dayjs(initialValues.time, "h:mm a") : null,
          pickupVenue: initialValues.pickup || initialValues.pickupVenue || "",
          dropoffLocation: initialValues.dest || initialValues.dropoffLocation || "",
          purpose: initialValues.purpose || initialValues.remarks || "",
        });
      } else {
        form.resetFields();
        if (initialDate) {
          form.setFieldsValue({
            tripDate: initialDate
          });
        }
      }
    }
  }, [isOpen, initialValues, initialDate, form]);

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  const handleAddCompanions = (newEmployees) => {
    setCompanions(prev => [...prev, ...newEmployees]);
    setIsInviteOpen(false);
  };

  const handleRemoveCompanion = (id) => {
    setCompanions(prev => prev.filter(c => c.id !== id));
  };

  const totalPassengers = 1 + companions.length;
  const needsMorePassengers = !initialValues && totalPassengers < MIN_TOTAL_PASSENGERS;

  const handleSubmit = async (values) => {
    const { tripDate, pickupTime, ...otherValues } = values;
    const formattedValues = {
      ...otherValues,
      trip_date: tripDate ? tripDate.format("YYYY-MM-DD") : null,
      estimated_pickup_time: pickupTime ? pickupTime.format("HH:mm:ss") : null,
      pickup_venue: sanitizeInput(values.pickupVenue),
      dropoff_location: sanitizeInput(values.dropoffLocation),
      trip_purpose: sanitizeInput(values.purpose),
    };

    if (!initialValues && companions.length > 0) {
      formattedValues.passengers = companions.map(c => ({
        employee_id: c.is_external ? null : c.id,
        is_external: !!c.is_external,
        name: c.name || c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        email: c.email,
        phone: c.phone,
      }));
    }

    setLoading(true);
    try {
      if (onSave) {
        await onSave(formattedValues);
      }
      form.resetFields();
      setCompanions([]);
    } catch (e) {
      // Error is handled by parent, but we re-throw to prevent resetFields if parent didn't catch it
      // Actually, if parent caught it and didn't re-throw, we just stop here.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span style={{ fontSize: '16px', fontWeight: 600 }}>Request Trip</span>}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={460}
      zIndex={1060}
      className="ouber-modal"
      styles={{
        body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', margin: '0 -24px', padding: '0 24px' }
      }}
    >
      <div style={{ marginTop: "20px" }}>
        <Text type="secondary" style={{ display: "block", marginBottom: "16px", fontSize: '12px', lineHeight: '1.4' }}>
          {initialValues 
            ? "Edit your custom trip request schedule below."
            : "Can't find a scheduled trip that fits your needs? Submit a request for a custom schedule."}
        </Text>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div style={{ display: "flex", gap: "16px" }}>
          <Form.Item 
            label="Trip Date" 
            name="tripDate" 
            rules={[{ required: true, message: 'Input cannot be empty' }]}
            style={{ flex: 1 }}
          >
            <DatePicker 
              style={{ width: "100%" }} 
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item 
            label="Pickup Time" 
            name="pickupTime" 
            rules={[{ required: true, message: 'Input cannot be empty' }]}
            style={{ flex: 1 }}
          >
            <TimePicker format="h:mm a" style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item 
          label="Pickup Venue" 
          name="pickupVenue"
          rules={[{ required: true, whitespace: true, message: 'Input cannot be empty' }]}
        >
          <Input placeholder="E.g., UPLB Gate" />
        </Form.Item>

        <Form.Item 
          label="Dropoff Location" 
          name="dropoffLocation"
          rules={[{ required: true, whitespace: true, message: 'Input cannot be empty' }]}
        >
          <Input placeholder="E.g., UP Diliman" />
        </Form.Item>

        <Form.Item 
          label="Purpose of Trip" 
          name="purpose"
          rules={[{ required: true, whitespace: true, message: 'Input cannot be empty' }]}
        >
          <TextArea rows={3} placeholder="Provide details about your official business" />
        </Form.Item>

        {userUnits.length > 1 && (
          <Form.Item
            label={<span>Unit to Represent <Text type="danger">*</Text></span>}
            name="unit"
            rules={[{ required: true, message: 'Please select which unit you represent' }]}
          >
            <Select placeholder="Select unit...">
              {userUnits.map(unit => (
                <Select.Option key={unit} value={unit}>{unit}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {!initialValues && (
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <Text>Companions</Text>
              <Button size="small" icon={<PlusOutlined />} onClick={() => setIsInviteOpen(true)}>
                Add Companions
              </Button>
            </div>

            <Text type="secondary" style={{ display: "block", fontSize: '12px', marginBottom: '8px' }}>
              Optional — a trip with fewer than {MIN_TOTAL_PASSENGERS} passengers total (including you) is unlikely to justify dispatching a vehicle on its own.
            </Text>

            {companions.length > 0 && (
              <List
                size="small"
                bordered
                dataSource={companions}
                rowKey={(item) => item.id}
                style={{ marginBottom: '8px', maxHeight: '160px', overflowY: 'auto' }}
                renderItem={item => {
                  const name = item.name || item.full_name || `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Unknown";
                  return (
                    <List.Item
                      actions={[<Button type="text" danger size="small" icon={<CloseOutlined />} onClick={() => handleRemoveCompanion(item.id)} />]}
                    >
                      <List.Item.Meta title={name} description={item.email} />
                    </List.Item>
                  );
                }}
              />
            )}

            {needsMorePassengers && (
              <Text type="warning" style={{ fontSize: '12px', display: 'block' }}>
                Only {totalPassengers} passenger{totalPassengers === 1 ? "" : "s"} so far ({totalPassengers}/{MIN_TOTAL_PASSENGERS}) — requests this small are likely to be declined unless you add {MIN_TOTAL_PASSENGERS - totalPassengers} more passenger{MIN_TOTAL_PASSENGERS - totalPassengers === 1 ? "" : "s"}. However, you can still submit.
              </Text>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="primary" htmlType="submit" className="ouber-btn-primary" loading={loading}>
            {initialValues ? "Save Changes" : "Request Trip"}
          </Button>
        </div>
      </Form>

      <AdminInviteEmployeeModal
        open={isInviteOpen}
        onCancel={() => setIsInviteOpen(false)}
        onAddEmployees={handleAddCompanions}
        existingPassengerIds={[...companions.map(c => c.id), currentUser?.id].filter(Boolean)}
        showUnit={false}
        emailRequired={false}
        requirePhone={true}
      />
    </div>
  </Modal>
  );
};

export default UserRequestTripModal;
